import { useState, type ReactNode } from 'react'
import { Button, Stack, Text } from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { StepHeader } from '@/features/transfer/components/StepHeader'
import { Pinput } from '@/shared/components/Pinput'
import { ErrorMessage } from '@/shared/components/ErrorMessage'
import { OTP_LENGTH } from '@/features/transfer/model/constants'
import styles from './SmsStep.module.css'

type SmsStepProps = {
  value: string
  onChange: (next: string) => void
  onBack: () => void
  onCancel: () => void
  onConfirm: () => void
  isSubmitting?: boolean
  /** Server-side error from the OTP confirmation call; takes precedence over the incomplete-code hint. */
  error?: ReactNode
}

export const SmsStep = ({
  value,
  onChange,
  onBack,
  onCancel,
  onConfirm,
  isSubmitting = false,
  error,
}: SmsStepProps) => {
  const { t } = useTranslation()
  const [showIncomplete, setShowIncomplete] = useState(false)
  const isComplete = value.length === OTP_LENGTH

  const handleConfirm = () => {
    if (isSubmitting) return
    if (!isComplete) {
      setShowIncomplete(true)
      return
    }
    setShowIncomplete(false)
    onConfirm()
  }

  return (
    <Stack direction='vertical' space='32'>
      <StepHeader title={t('transfer.smsTitle')} onBack={onBack} />

      <Stack direction='vertical' space='12'>
        <Text>{t('transfer.sms.sentText')}</Text>
        <div className={styles.codeField}>
          <Pinput
            length={OTP_LENGTH}
            value={value}
            onChange={next => {
              onChange(next)
              if (showIncomplete) setShowIncomplete(false)
            }}
            label={t('transfer.sms.codeLabel')}
            disabled={isSubmitting}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
        {error ? (
          <ErrorMessage id='sms-error'>{error}</ErrorMessage>
        ) : showIncomplete && !isComplete ? (
          <ErrorMessage id='sms-incomplete'>
            {t('transfer.sms.incomplete', { count: OTP_LENGTH })}
          </ErrorMessage>
        ) : null}
      </Stack>

      <Stack direction='horizontal' space='24' align='center'>
        <Button
          type='button'
          variant='secondary'
          color='teal'
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('transfer.sms.cancelButton')}
        </Button>
        <Button
          type='button'
          variant='primary'
          color='teal'
          onClick={handleConfirm}
          loading={isSubmitting}
        >
          {t('transfer.sms.confirmButton')}
        </Button>
      </Stack>
    </Stack>
  )
}
